// Web entry point — full implementation for browser (V8 / Chrome).
export type { UserTimezone, DateFormatMode } from './DateTimeFormatter.base';
import type { UserTimezone, DateFormatMode } from './DateTimeFormatter.base';
import {
  parseOffsetMinutes,
  formatDateFromIana,
  formatDateFromOffset,
} from './DateTimeFormatter.base';

/**
 * Computes the UTC offset of an IANA zone at a given UTC millisecond timestamp.
 * Returns offset in minutes (e.g. -300 for UTC-5, +330 for UTC+5:30).
 *
 * Web-specific: uses Intl.DateTimeFormat.formatToParts which is more ergonomic
 * on V8/Chrome than the toLocaleString approach used on native (Hermes/JSC).
 */
const getIanaOffsetMinutes = (ianaZone: string, utcMs: number): number => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(new Date(utcMs));
  const v = (t: string) => Number.parseInt(parts.find(p => p.type === t)?.value ?? '0', 10);
  const h = v('hour') % 24;
  const localMs = Date.UTC(v('year'), v('month') - 1, v('day'), h, v('minute'), v('second'));
  return (localMs - utcMs) / 60_000;
};

// In-memory cache: lives only for the lifetime of the current browser tab /
// app process.  It is never persisted, so there is no version-bumping concern.
const ianaCache: Record<string, string | null> = {};

/** Returns true when `zone`'s January UTC offset matches `stdOffsetMinutes`. */
const matchesStdOffset = (zone: string, stdOffsetMinutes: number, janProbeMs: number): boolean => {
  try {
    return getIanaOffsetMinutes(zone, janProbeMs) === stdOffsetMinutes;
  } catch {
    return false;
  }
};

interface ZoneScore {
  score: number;
  shortScore: number;
  extraCount: number;
  isDst: boolean;
}

/**
 * Scores a single candidate zone against the hint-word / acronym / DST heuristics.
 * Returns null if the zone throws during introspection (skipped).
 */
const scoreZoneCandidate = (
  zone: string,
  hintWords: string[],
  keyNameTokens: Set<string>,
  keyAcronym: string,
  stdOffsetMinutes: number,
  janProbeMs: number,
  julProbeMs: number,
): ZoneScore | null => {
  try {
    const longNameRaw = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'long' })
      .formatToParts(new Date(janProbeMs))
      .find(p => p.type === 'timeZoneName')
      ?.value ?? '';

    // Score: how many hint words appear in the long name + zone identifier.
    const combined = longNameRaw.toLowerCase() + ' ' + zone.toLowerCase();
    const score = hintWords.filter(w => combined.includes(w)).length;

    // Tiebreaker A: short-name abbreviation match against keyName acronym.
    // Pacific/Honolulu → "HST" matches keyAcronym "HST" for "Hawaiian Standard Time".
    // America/Adak    → "HAST" does NOT match → Honolulu wins in modern Chrome
    // where both zones share the same long name "Hawaii-Aleutian Standard Time".
    const shortNameRaw = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(new Date(janProbeMs))
      .find(p => p.type === 'timeZoneName')
      ?.value ?? '';
    const shortScore = shortNameRaw === keyAcronym ? 1 : 0;

    // Tiebreaker B: long-name tokens absent from keyNameTokens (fewer = better).
    const longNameTokens = longNameRaw.toLowerCase().split(/[\s-]+/).filter(Boolean);
    const extraCount = longNameTokens.filter(t => !keyNameTokens.has(t)).length;

    // Tiebreaker C: prefer DST-observing zone (handles Central/Eastern/Mountain/Pacific).
    let isDst = false;
    try {
      isDst = getIanaOffsetMinutes(zone, julProbeMs) !== stdOffsetMinutes;
    } catch { /* treat as non-DST */ }

    return { score, shortScore, extraCount, isDst };
  } catch {
    return null; // skip zone
  }
};

/** Returns true when `candidate` is strictly better than the current `best`. */
const isBetterCandidate = (
  candidate: ZoneScore,
  best: { score: number; shortScore: number; extraCount: number; isDst: boolean },
): boolean => {
  if (candidate.score !== best.score) return candidate.score > best.score;
  if (candidate.shortScore !== best.shortScore) return candidate.shortScore > best.shortScore;
  if (candidate.extraCount !== best.extraCount) return candidate.extraCount < best.extraCount;
  return candidate.isDst && !best.isDst;
};

const resolveIanaZone = (
  keyName: string,
  stdOffsetMinutes: number,
  displayName: string,
): string | null => {
  if (keyName in ianaCache) {
    return ianaCache[keyName] ?? null;
  }

  try {
    const supportedFn = (Intl as unknown as Record<string, unknown>).supportedValuesOf;
    if (typeof supportedFn !== 'function') {
      ianaCache[keyName] = null;
      return null;
    }

    const allZones = (supportedFn as (key: string) => string[])('timeZone');
    const janProbeMs = Date.UTC(new Date().getUTCFullYear(), 0, 15, 12, 0, 0);
    const julProbeMs = Date.UTC(new Date().getUTCFullYear(), 6, 15, 12, 0, 0);

    const candidates = allZones.filter(zone => matchesStdOffset(zone, stdOffsetMinutes, janProbeMs));

    if (!candidates.length) {
      ianaCache[keyName] = null;
      return null;
    }

    const hintWords = displayName
      .replace(/[()+-]/g, ' ') // NOSONAR
      .toLowerCase()
      .split(/[\s,&/]+/)
      .filter(w => w.length > 3 && !/^(gmt|utc|\d)/.test(w));

    // keyName token set for Tiebreaker A.
    // "Hawaiian Standard Time" → {"hawaiian","standard","time"}
    const keyNameTokens = new Set(keyName.toLowerCase().split(/[\s-]+/).filter(Boolean));

    // Acronym from keyName used to match timezone abbreviations.
    // "Hawaiian Standard Time" → "HST", "Central Standard Time" → "CST", etc.
    const keyAcronym = keyName.split(/\s+/).map(w => w[0]?.toUpperCase() ?? '').join('');

    let bestZone: string = candidates[0]!;
    let best = { score: -1, shortScore: -1, extraCount: Infinity, isDst: false };

    for (const zone of candidates) {
      const zs = scoreZoneCandidate(zone, hintWords, keyNameTokens, keyAcronym, stdOffsetMinutes, janProbeMs, julProbeMs);
      if (zs !== null && isBetterCandidate(zs, best)) {
        best     = { score: zs.score, shortScore: zs.shortScore, extraCount: zs.extraCount, isDst: zs.isDst };
        bestZone = zone;
      }
    }

    // Only return a dynamic IANA zone when the best-matching candidate observes DST.
    // If the winner is non-DST (Pacific/Honolulu for "Hawaiian Standard Time"),
    // the static offset from displayName is always correct — return null so the
    // static fallback is used.  This also fires when no hint word matched (best.score ≤ 0),
    // which is correct for Arizona, Saskatchewan, India, Nepal, etc.
    if (best.score <= 0 || !best.isDst) {
      ianaCache[keyName] = null;
      return null;
    }

    ianaCache[keyName] = bestZone;
    return bestZone;
  } catch {
    ianaCache[keyName] = null;
    return null;
  }
};

/**
 * Formats an API date string as "MM/DD/YYYY HH:MM AM/PM".
 * Uses the same timezone resolution as `formatDate`.
 *
 * @example
 *   formatDateShort('2026-10-13T14:45:00', userTimezone)
 *   // → "10/13/2026 02:45 PM"
 */
export const formatDateShort = (
  apiDate: string,
  timezone: UserTimezone,
): string => formatDate(apiDate, timezone, 'shortDateTime');

export const formatDate = (
  apiDate: string,
  timezone: UserTimezone,
  mode: DateFormatMode = 'dateTime',
): string => {
  if (!apiDate) return '';

  const utcString = /Z$|[+-]\d{2}:\d{2}$/.test(apiDate) ? apiDate : `${apiDate}Z`;
  const utcMs = Date.parse(utcString);
  if (Number.isNaN(utcMs)) return '';

  const stdOffsetMin = parseOffsetMinutes(timezone.displayName ?? '');

  const ianaZone = timezone.keyName
    ? resolveIanaZone(timezone.keyName, stdOffsetMin, timezone.displayName ?? '')
    : null;

  if (ianaZone) {
    const result = formatDateFromIana(ianaZone, utcMs, mode);
    if (result !== null) return result;
  }

  return formatDateFromOffset(utcMs, stdOffsetMin, mode);
};